/**
 * Dynamic GraphQL query generator for Jira issue → PR hydration.
 *
 * Uses GraphQL aliases to batch multiple issue ARI lookups into a single
 * AGG request to devOps.ariGraph.issueAssociatedPr.
 *
 * The GraphStore schema only supports single-ID lookups (id: ID!), so we use
 * GraphQL aliases to batch multiple lookups. The number of issues per query
 * is capped at MAX_ISSUES_PER_QUERY to prevent unbounded query sizes.
 *
 * Example generated query:
 * ```graphql
 * query SearchPageJiraPRHydration($issueAri0: ID!, $issueAri1: ID!) {
 *   graphStore @optIn(to: "GraphStore") {
 *     issue0: issueAssociatedPr(id: $issueAri0, first: 10)
 *       @optIn(to: "GraphStoreIssueAssociatedPr") {
 *       edges { id node { ... on DevOpsPullRequestDetails { ... } } }
 *     }
 *     issue1: issueAssociatedPr(id: $issueAri1, first: 10)
 *       @optIn(to: "GraphStoreIssueAssociatedPr") { ... }
 *   }
 * }
 * ```
 */

/** Maximum number of PRs to fetch per issue */
export const PR_LIMIT_PER_ISSUE = 10;

/**
 * Maximum number of issues that can be included in a single batched query.
 * The fetch layer chunks larger sets into multiple parallel requests.
 */
export const MAX_ISSUES_PER_QUERY = 100;

/** Variables for the PR hydration query, keyed as issueAri0, issueAri1, etc. */
export type SearchPageJiraPRHydrationQueryVariables = {
	[key: `issueAri${number}`]: string;
};

const prFragment = `
	edges {
		id
		node {
			... on DevOpsPullRequestDetails {
				id
				title
				url
				pullRequestInternalId
				status
				lastUpdated
				reviewers {
					approvalStatus
				}
			}
		}
	}
`;

/**
 * Generates a GraphQL query that batches multiple issueAssociatedPr calls
 * using aliases. Each issue ARI gets its own aliased field.
 *
 * @param issueAris - Array of Jira issue ARIs to look up
 * @returns The generated GraphQL query string
 */
export const jiraPRQueryGenerator = (issueAris: string[]): string => {
	if (issueAris.length === 0) {
		throw new Error('jiraPRQueryGenerator requires at least one issue ARI');
	}

	if (issueAris.length > MAX_ISSUES_PER_QUERY) {
		throw new Error(
			`jiraPRQueryGenerator received ${issueAris.length} issue ARIs, but the maximum is ${MAX_ISSUES_PER_QUERY}. Use the fetch layer to chunk large batches.`,
		);
	}

	const variableDeclarations = issueAris.map((_, index) => `$issueAri${index}: ID!`).join(', ');

	const aliasedFields = issueAris
		.map(
			(_, index) => `
			issue${index}: issueAssociatedPr(id: $issueAri${index}, first: ${PR_LIMIT_PER_ISSUE})
				@optIn(to: "GraphStoreIssueAssociatedPr") {
				${prFragment}
			}`,
		)
		.join('\n');

	return `
	query SearchPageJiraPRHydration(${variableDeclarations}) {
		graphStore @optIn(to: "GraphStore") {
			${aliasedFields}
		}
	}
	`;
};

/**
 * Builds the variables object for the generated PR hydration query.
 *
 * @param issueAris - Array of Jira issue ARIs
 * @returns Variables object mapping issueAri0, issueAri1, etc. to ARI values
 */
export const buildPRQueryVariables = (
	issueAris: string[],
): SearchPageJiraPRHydrationQueryVariables => {
	const variables: SearchPageJiraPRHydrationQueryVariables = {};
	issueAris.forEach((ari, index) => {
		variables[`issueAri${index}`] = ari;
	});
	return variables;
};
